import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zonesAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';

const zoneTypeOptions = [
  { value: 'general', label: 'General Area' },
  { value: 'restricted', label: 'Restricted Zone' },
  { value: 'hazard', label: 'Hazard Zone' },
  { value: 'crane_zone', label: 'Crane Zone' },
  { value: 'excavation', label: 'Excavation' },
  { value: 'chemical', label: 'Chemical Handling' },
  { value: 'electrical', label: 'Electrical Hazard' },
  { value: 'confined_space', label: 'Confined Space' },
  { value: 'hot_work', label: 'Hot Work Zone' },
  { value: 'safe_zone', label: 'Safe Zone' },
];

const riskLevelOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const vehicleOptions = [
  { value: 'allowed', label: 'Allowed' },
  { value: 'restricted', label: 'Restricted' },
  { value: 'prohibited', label: 'Prohibited' },
];

const ppeOptions = [
  { value: 'hard_hat', label: 'Hard Hat' },
  { value: 'safety_vest', label: 'Safety Vest' },
  { value: 'safety_glasses', label: 'Safety Glasses' },
  { value: 'gloves', label: 'Gloves' },
  { value: 'safety_boots', label: 'Safety Boots' },
  { value: 'face_mask', label: 'Face Mask' },
  { value: 'ear_protection', label: 'Ear Protection' },
  { value: 'harness', label: 'Safety Harness' },
  { value: 'respirator', label: 'Respirator' },
];

const colorOptions = [
  '#3B82F6', '#EF4444', '#F97316', '#EAB308', '#22C55E',
  '#8B5CF6', '#EC4899', '#6B7280', '#14B8A6', '#F59E0B',
];

export default function ZoneFormModal({ zone, siteId, onClose }) {
  const queryClient = useQueryClient();
  const isEditing = !!zone;

  const [formData, setFormData] = useState({
    siteId: zone?.siteId || siteId || '',
    name: zone?.name || '',
    code: zone?.code || '',
    type: zone?.type || 'general',
    color: zone?.color || '#3B82F6',
    riskLevel: zone?.riskLevel || 'medium',
    maxOccupancy: zone?.maxOccupancy ?? '',
    minSupervisors: zone?.minSupervisors ?? '',
    vehicleRestriction: zone?.vehicleRestriction || 'allowed',
    speedLimit: zone?.speedLimit ?? '',
    geofenceBuffer: zone?.geofenceBuffer ?? '',
    alertOnEntry: zone?.alertOnEntry ?? false,
    alertOnExit: zone?.alertOnExit ?? false,
    alertOnOvercrowd: zone?.alertOnOvercrowd ?? true,
    alertOnPPE: zone?.alertOnPPE ?? true,
    alertOnVehicle: zone?.alertOnVehicle ?? false,
    ppeRequired: zone?.ppeRequirements?.required || [],
    ppeConditional: zone?.ppeRequirements?.conditional || [],
  });

  const createMutation = useMutation({
    mutationFn: (data) => zonesAPI.createZone(data),
    onSuccess: () => {
      toast.success('Zone created successfully');
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create zone');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) => zonesAPI.updateZone(zone.id, data),
    onSuccess: () => {
      toast.success('Zone updated successfully');
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update zone');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      polygon: zone?.polygon || { type: 'polygon', coordinates: [] },
      maxOccupancy: formData.maxOccupancy ? parseInt(formData.maxOccupancy) : null,
      minSupervisors: formData.minSupervisors ? parseInt(formData.minSupervisors) : null,
      speedLimit: formData.speedLimit ? parseInt(formData.speedLimit) : null,
      geofenceBuffer: formData.geofenceBuffer ? parseFloat(formData.geofenceBuffer) : null,
      ppeRequirements: {
        required: formData.ppeRequired,
        conditional: formData.ppeConditional,
      },
    };
    delete data.ppeRequired;
    delete data.ppeConditional;

    if (isEditing) {
      delete data.siteId;
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const togglePPE = (list, value) => {
    const key = list === 'required' ? 'ppeRequired' : 'ppeConditional';
    const current = formData[key];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setFormData({ ...formData, [key]: updated });
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="modal-backdrop">
      <div className="modal-container" onClick={onClose}>
        <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">
              {isEditing ? 'Edit Zone' : 'Add New Zone'}
            </h3>
            <button onClick={onClose} className="icon-btn">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Name & Code */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Zone Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    placeholder="Loading Bay A"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Zone Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="input"
                    placeholder="ZONE-A1"
                    required
                    disabled={isEditing}
                  />
                </div>
              </div>

              {/* Type & Risk */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Zone Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="input"
                  >
                    {zoneTypeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Risk Level</label>
                  <select
                    value={formData.riskLevel}
                    onChange={(e) => setFormData({ ...formData, riskLevel: e.target.value })}
                    className="input"
                  >
                    {riskLevelOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Color</label>
                <div className="flex gap-2">
                  {colorOptions.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={clsx(
                        'w-8 h-8 rounded-lg transition-all',
                        formData.color === color && 'ring-2 ring-offset-2 ring-blue-500 scale-110'
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Occupancy & Supervisors */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Max Occupancy</label>
                  <input
                    type="number"
                    value={formData.maxOccupancy}
                    onChange={(e) => setFormData({ ...formData, maxOccupancy: e.target.value })}
                    className="input"
                    placeholder="50"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Min Supervisors</label>
                  <input
                    type="number"
                    value={formData.minSupervisors}
                    onChange={(e) => setFormData({ ...formData, minSupervisors: e.target.value })}
                    className="input"
                    placeholder="1"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Geofence Buffer (m)</label>
                  <input
                    type="number"
                    value={formData.geofenceBuffer}
                    onChange={(e) => setFormData({ ...formData, geofenceBuffer: e.target.value })}
                    className="input"
                    placeholder="5"
                    min="0"
                    step="0.5"
                  />
                </div>
              </div>

              {/* Vehicle Restriction & Speed */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Vehicle Restriction</label>
                  <select
                    value={formData.vehicleRestriction}
                    onChange={(e) => setFormData({ ...formData, vehicleRestriction: e.target.value })}
                    className="input"
                  >
                    {vehicleOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Speed Limit (km/h)</label>
                  <input
                    type="number"
                    value={formData.speedLimit}
                    onChange={(e) => setFormData({ ...formData, speedLimit: e.target.value })}
                    className="input"
                    placeholder="10"
                    min="0"
                  />
                </div>
              </div>

              {/* PPE Required */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Required PPE</label>
                <div className="flex flex-wrap gap-2">
                  {ppeOptions.map(ppe => (
                    <button
                      key={ppe.value}
                      type="button"
                      onClick={() => togglePPE('required', ppe.value)}
                      className={clsx(
                        'badge cursor-pointer transition-all',
                        formData.ppeRequired.includes(ppe.value)
                          ? 'badge-critical'
                          : 'badge-neutral opacity-60 hover:opacity-100'
                      )}
                    >
                      {ppe.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* PPE Conditional */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Conditional PPE</label>
                <div className="flex flex-wrap gap-2">
                  {ppeOptions.map(ppe => (
                    <button
                      key={ppe.value}
                      type="button"
                      onClick={() => togglePPE('conditional', ppe.value)}
                      className={clsx(
                        'badge cursor-pointer transition-all',
                        formData.ppeConditional.includes(ppe.value)
                          ? 'badge-warning'
                          : 'badge-neutral opacity-60 hover:opacity-100'
                      )}
                    >
                      {ppe.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Alert Settings */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-3">Alert Settings</label>
                <div className="space-y-3">
                  {[
                    { key: 'alertOnEntry', label: 'Alert on zone entry' },
                    { key: 'alertOnExit', label: 'Alert on zone exit' },
                    { key: 'alertOnOvercrowd', label: 'Alert on overcrowding' },
                    { key: 'alertOnPPE', label: 'Alert on PPE violation' },
                    { key: 'alertOnVehicle', label: 'Alert on vehicle violation' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center justify-between cursor-pointer group">
                      <span className="text-sm text-slate-600 group-hover:text-slate-800">{label}</span>
                      <input
                        type="checkbox"
                        checked={formData[key]}
                        onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </span>
                ) : (
                  isEditing ? 'Update Zone' : 'Create Zone'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
